import { DateTime } from 'luxon'
import {
  isAvailableDomesticAccommodationOption,
  isAvailableDomesticBusOption,
  isAvailableDomesticLeisureOption,
  isAvailableDomesticTrainOption,
} from '~/app/components/Cart/CartOrderHelper'
import type { AccommodationOption } from '~/app/components/Cart/CartItem/DomesticAccommodationItem'
import type { TrainOption } from '~/app/components/Cart/CartItem/DomesticTrainItem'
import type { LeisureOption } from '~/app/components/Cart/CartItem/DomesticLeisureItem'
import type { BusOption } from '~/app/components/Cart/CartItem/DomesticBusItem'
import conf from '~/app/conf'
import { lpad } from '~/api/utils/train'

export const KTX_TRAIN_GROUP_CODE = '100'

export interface IOptionPrice {
  price: number
  packageDiscount?: number
  isKTX?: boolean
  departureAt?: string
}

export function overtimeHourNotation(runningDate: string, arrivalAt: string) {
  const HOUR_IN_MINUTES = 60
  const std = DateTime.fromFormat(runningDate, 'yyyy-MM-dd')
  const target = DateTime.fromISO(arrivalAt)
  const diffInMinutes = target.diff(std, 'minutes').toObject()
  const hours = Math.floor(diffInMinutes.minutes! / HOUR_IN_MINUTES)
  return `${lpad(hours, 2, '0')}:${lpad(diffInMinutes.minutes! % HOUR_IN_MINUTES, 2)}`
}

//
// 묶음 할인 프로모션 기간 확인 여부 주석 처리
// function getIsBetweenDates(startAt: string, endAt: string, targetAt?: string): boolean {
//   const currentDateTime = targetAt ? DateTime.fromISO(targetAt) : DateTime.now()
//   const startDateTime = DateTime.fromISO(startAt)
//   const endDateTime = DateTime.fromISO(endAt)
//
//   return startDateTime <= currentDateTime && endDateTime > currentDateTime
// }

/**
 * @Summary       KORAIL 및 사내 정책을 따르는 묶음할인 가격을 계산하여 반환하는 함수
 * @Description   정책은 다음과 같다.
 *                1. 승차권 별 묶음할인율이 1% 이상인 경우만 할인 적용 대상이다.
 *                2. 묶음할인 상품이 포함된 상품들은 기차 상품 정상 운임가의 100% 이상으로 판매 가능하다
 *                  2-1. [FES-1200] 2022 여행가는 달 프로모션 기간 동안 기차 상품 정상 운임가의 80% 이상으로 판매가능하다
 *                    2-1-1. 해당 정책은 KTX 열차에 한정하여 적용한다.
 *                  2-2. 2-1 정책은 프로모션 기간이 끝나도 유지될 가능성이 있다.
 *
 * @param         {Array<IOptionPrice>} optionPrices 옵션 가격을 가진 객체를 원소로 갖는 배열
 * @return        {number} 최종 묶음할인 가격. 0이라면 묶음할인 적용 대상이 아니다
 * */

export function getTrainPackageDiscountAmount(optionPrices: IOptionPrice[]): number {
  let totalPackageDiscount = 0
  const optionPriceWithoutDiscountedTrain = optionPrices.reduce((acc, cur) => {
    // 승차권 별 묶음할인율이 1% 이상인 경우만 총 묶음할인가격에 대해준다.
    if (Math.round(((cur.packageDiscount || 0) / cur.price) * 100) > 0) {
      totalPackageDiscount += cur.packageDiscount || 0
      return acc
    }
    return acc + cur.price
  }, 0)
  return Math.min(totalPackageDiscount, optionPriceWithoutDiscountedTrain)
}

export type MergerCartItemOptions = Array<
  AccommodationOption | LeisureOption | TrainOption | BusOption
>
export const filterChosenAndAvailableOptions = (
  items: MergerCartItemOptions
): MergerCartItemOptions => {
  return items.filter(
    item =>
      item.isChosen &&
      (item.__typename === 'DomesticBusCartItemOption'
        ? isAvailableDomesticBusOption(item)
        : item.__typename === 'DomesticAccommodationCartItemOption'
          ? isAvailableDomesticAccommodationOption(item)
          : item.__typename === 'DomesticLeisureCartItemOption'
            ? isAvailableDomesticLeisureOption(item)
            : item.__typename === 'DomesticTrainCartItemOption'
              ? isAvailableDomesticTrainOption(item)
              : false)
  )
}

// 묶음할인을 계산하기 위해 장바구니 내 옵션들을 {IOptionPrice[]} 타입 1차 배열로 재구성하는 함수
export const mapTrainPackageDiscountOptions = (items: MergerCartItemOptions): IOptionPrice[] => {
  return items
    .filter(
      ({ __typename }) =>
        __typename === 'DomesticTrainCartItemOption' ||
        __typename === 'DomesticAccommodationCartItemOption'
    )
    .map(item =>
      item.__typename === 'DomesticTrainCartItemOption'
        ? item.trains.map(({ price, packageDiscount, trainGroupCode, departureAt }) => ({
            price,
            packageDiscount,
            isKTX: trainGroupCode === KTX_TRAIN_GROUP_CODE,
            departureAt,
          }))
        : { price: item.price, packageDiscount: 0 }
    )
    .flat()
}

export const getIsDisabledOrderTrains = ({
  propertyCount,
  leisureCount,
  trainCount,
  busCount,
}: {
  propertyCount: number
  leisureCount: number
  trainCount: number
  busCount: number
}): {
  isDisabledByOnlyTrains: boolean
  isDisabledByItems: boolean
  disabledOrderTrainMessage: string[]
} => {
  /**
   * 기차 상품 단독 구매 불가
   */
  const isDisabledByOnlyTrains = trainCount > 0 && leisureCount + propertyCount + busCount <= 0

  /**
   * 기차 상품 구매 조건
   * 숙소 + 기차 -> 구매 가능
   * 숙소 + 기차 + (레저 or 버스) -> 구매 불가능
   */
  const isDisabledByItems = trainCount > 0 && (propertyCount <= 0 || leisureCount + busCount > 0)
  const disabledOrderTrainMessage = [
    '기차는 단독으로 예약할 수 없어요',
    '기차는 국내숙소와 묶음예약만 가능해요.',
  ]

  return {
    isDisabledByOnlyTrains,
    isDisabledByItems,
    disabledOrderTrainMessage,
  }
}

export const getIsOrderTrainPropertyPolicy = (dateTime: string) => {
  return DateTime.fromISO(dateTime) > DateTime.fromISO(conf.trainPolicy.start)
}
